import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FiGift, FiHeart, FiCalendar, FiStar, FiSun, FiSmile } from 'react-icons/fi';

const Section = styled.section`
  padding: 100px 0;
  background: linear-gradient(135deg, var(--light-gray) 0%, #f1f5f9 50%, #e2e8f0 100%);
  &#life-events {
    scroll-margin-top: 80px;
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
`;

const SectionTitle = styled(motion.h2)`
  text-align: center;
  font-size: 3rem;
  color: var(--primary-blue);
  margin-bottom: 24px;

  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const SectionSubtitle = styled(motion.p)`
  text-align: center;
  font-size: 1.3rem;
  color: var(--medium-gray);
  max-width: 800px;
  margin: 0 auto 60px;
  line-height: 1.6;
`;

const EventsGrid = styled(motion.div)`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 40px;
  margin-bottom: 60px;
`;

const EventCard = styled(motion.div)`
  background: white;
  padding: 40px 30px;
  border-radius: 16px;
  text-align: center;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.08);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  border-top: 4px solid var(--primary-orange);

  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 15px 40px rgba(0, 0, 0, 0.12);
  }
`;

const EventIcon = styled.div`
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, var(--primary-orange), var(--warm-gold));
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 24px;
  color: white;
  font-size: 2rem;
`;

const EventTitle = styled.h4`
  font-size: 1.5rem;
  color: var(--primary-blue);
  margin-bottom: 16px;
  font-weight: 600;
`;

const EventDescription = styled.p`
  font-size: 1rem;
  line-height: 1.6;
  color: var(--medium-gray);
  margin-bottom: 20px;
`;

const EventExamples = styled.div`
  background: var(--light-gray);
  padding: 20px;
  border-radius: 8px;
  margin-top: 16px;
`;

const ExampleTitle = styled.h6`
  font-size: 0.9rem;
  color: var(--primary-blue);
  margin-bottom: 8px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const ExampleList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  text-align: center;
  
  li {
    font-size: 0.9rem;
    color: var(--medium-gray);
    margin-bottom: 6px;
    padding: 0;
    line-height: 1.4;
  }
`;

const TestimonialsSection = styled(motion.div)`
  background: white;
  border-radius: 16px;
  padding: 60px 40px;
  margin-top: 80px;
  text-align: center;
`;

const TestimonialsTitle = styled.h3`
  font-size: 2.5rem;
  color: var(--primary-blue);
  margin-bottom: 40px;
`;

const TestimonialsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 40px;
  margin-top: 40px;
`;

const TestimonialCard = styled.div`
  padding: 30px;
  background: var(--light-gray);
  border-radius: 12px;
  border-left: 4px solid var(--secondary-green);
`;

const TestimonialText = styled.p`
  font-style: italic;
  font-size: 1.1rem;
  line-height: 1.6;
  color: var(--dark-gray);
  margin-bottom: 20px;
`;

const TestimonialAuthor = styled.div`
  font-weight: 600;
  color: var(--primary-blue);
  font-size: 1rem;
`;

const TestimonialContext = styled.div`
  font-size: 0.9rem;
  color: var(--medium-gray);
  margin-top: 4px;
`;

const CallToAction = styled(motion.div)`
  text-align: center;
  margin-top: 60px;
  padding: 60px 40px;
  background: linear-gradient(135deg, var(--secondary-green) 0%, #10B981 50%, #047857 100%);
  border-radius: 16px;
  color: white;
  
  * {
    color: white !important;
  }
`;

const CTATitle = styled.h3`
  font-size: 2.2rem;
  margin-bottom: 20px;
  color: white !important;
`;

const CTAText = styled.p`
  font-size: 1.2rem;
  margin-bottom: 30px;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
  opacity: 1;
  color: white !important;
`;

const CTAButton = styled(motion.button)`
  background: white;
  color: var(--secondary-green) !important;
  border: none;
  padding: 16px 32px;
  border-radius: 8px;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  transition: transform 0.3s ease;

  &:hover {
    transform: translateY(-2px);
  }
`;

const LifeEvents = () => {
  const scrollToContact = () => {
    const element = document.getElementById('contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <Section id="life-events">
      <Container>
        <SectionTitle
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          Celebrate Life's Moments with Service
        </SectionTitle>
        
        <SectionSubtitle
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          viewport={{ once: true }}
        >
          Turn meaningful moments into opportunities to serve others. Sponsor an Ahaar Sewa food program 
          to honor special occasions and create lasting positive impact in our community.
        </SectionSubtitle>

        <EventsGrid
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.3 }}
          viewport={{ once: true }}
        >
          <EventCard>
            <EventIcon>
              <FiGift />
            </EventIcon>
            <EventTitle>Birthday Celebrations</EventTitle>
            <EventDescription>
              Honor your special day or a loved one's birthday by feeding the community. 
              It's a beautiful way to start a new year of life with service and gratitude.
            </EventDescription>
            <EventExamples>
              <ExampleTitle>Popular Choices:</ExampleTitle>
              <ExampleList>
                <li>Children's milestone birthdays</li>
                <li>Adult milestone celebrations (30th, 50th, etc.)</li>
                <li>Family birthday traditions</li>
                <li>Corporate team member birthdays</li>
              </ExampleList>
            </EventExamples>
          </EventCard>

          <EventCard>
            <EventIcon>
              <FiHeart />
            </EventIcon>
            <EventTitle>In Memory & Honor</EventTitle>
            <EventDescription>
              Remember loved ones or honor their legacy by serving others in their name. 
              Create a meaningful tribute that continues their spirit of giving.
            </EventDescription>
            <EventExamples>
              <ExampleTitle>Meaningful Occasions:</ExampleTitle>
              <ExampleList>
                <li>Memorial services</li>
                <li>Anniversary of passing</li>
                <li>Honoring parents & grandparents</li>
                <li>Celebrating recovered health</li>
              </ExampleList>
            </EventExamples>
          </EventCard>

          <EventCard>
            <EventIcon>
              <FiCalendar />
            </EventIcon>
            <EventTitle>Special Anniversaries</EventTitle>
            <EventDescription>
              Celebrate wedding anniversaries, work milestones, or other significant dates 
              by giving back to the community together.
            </EventDescription>
            <EventExamples>
              <ExampleTitle>Anniversary Types:</ExampleTitle>
              <ExampleList>
                <li>Wedding anniversaries</li>
                <li>Business founding dates</li>
                <li>Immigration anniversaries</li>
                <li>Recovery milestones</li>
              </ExampleList>
            </EventExamples>
          </EventCard>

          <EventCard>
            <EventIcon>
              <FiStar />
            </EventIcon>
            <EventTitle>Achievement Celebrations</EventTitle>
            <EventDescription>
              Mark graduations, promotions, awards, or personal achievements by 
              sharing your success through service to others.
            </EventDescription>
            <EventExamples>
              <ExampleTitle>Achievements:</ExampleTitle>
              <ExampleList>
                <li>Graduation ceremonies</li>
                <li>Job promotions</li>
                <li>Awards & recognitions</li>
                <li>Personal goal completions</li>
              </ExampleList>
            </EventExamples>
          </EventCard>

          <EventCard>
            <EventIcon>
              <FiSun />
            </EventIcon>
            <EventTitle>Festival & Holiday Giving</EventTitle>
            <EventDescription>
              Celebrate cultural festivals, religious holidays, or seasonal occasions 
              by extending the joy to those in need.
            </EventDescription>
            <EventExamples>
              <ExampleTitle>Celebrations:</ExampleTitle>
              <ExampleList>
                <li>Diwali, Eid, Christmas</li>
                <li>New Year celebrations</li>
                <li>Thanksgiving gratitude</li>
                <li>Cultural heritage months</li>
              </ExampleList>
            </EventExamples>
          </EventCard>

          <EventCard>
            <EventIcon>
              <FiSmile />
            </EventIcon>
            <EventTitle>Just Because</EventTitle>
            <EventDescription>
              Sometimes the best reason to give is simply because you can. Sponsor an event 
              to spread joy and make a difference any day of the year.
            </EventDescription>
            <EventExamples>
              <ExampleTitle>Reasons:</ExampleTitle>
              <ExampleList>
                <li>Random acts of kindness</li>
                <li>Meaningful corporate social responsibility</li>
                <li>Community connection</li>
                <li>Teaching children about service</li>
              </ExampleList>
            </EventExamples>
          </EventCard>
        </EventsGrid>

        <TestimonialsSection
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <TestimonialsTitle>Stories from Our Sponsors</TestimonialsTitle>
          
          <TestimonialsGrid>
            <TestimonialCard>
              <TestimonialText>
                "We sponsored an Ahaar Sewa Burrito program for my daughter's 16th birthday. Instead of a big party, 
              she wanted to help feed people. It was the most meaningful celebration we've ever had."
              </TestimonialText>
              <TestimonialAuthor>The Patel Family</TestimonialAuthor>
              <TestimonialContext>Sweet 16 Birthday Sponsor</TestimonialContext>
            </TestimonialCard>

            <TestimonialCard>
              <TestimonialText>
                "Every year on my father's birthday, we sponsor burritos in his memory. It keeps his 
                spirit of giving alive and brings our family together for something meaningful."
              </TestimonialText>
              <TestimonialAuthor>Rajesh Kumar</TestimonialAuthor>
              <TestimonialContext>Memorial Sponsor</TestimonialContext>
            </TestimonialCard>

            <TestimonialCard>
              <TestimonialText>
                "Our company celebrated our 10th anniversary by sponsoring a full burrito event. 
                The event perfectly reflected our company values and made a real difference in our community."
              </TestimonialText>
              <TestimonialAuthor>TechStart Solutions</TestimonialAuthor>
              <TestimonialContext>Corporate Anniversary Sponsor</TestimonialContext>
            </TestimonialCard>
          </TestimonialsGrid>
        </TestimonialsSection>

        <CallToAction
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <CTATitle>Turn Your Next Celebration into Service</CTATitle>
          <CTAText>
            Whether it's a birthday, anniversary, achievement, or just because – every occasion 
            becomes more meaningful when shared with those in need.
          </CTAText>
          <CTAButton
            onClick={scrollToContact}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            Plan Your Celebration
          </CTAButton>
        </CallToAction>
      </Container>
    </Section>
  );
};

export default LifeEvents; 